import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react'; // NATIVE A11Y CHANGE: useRef is needed to manage read-only focus targets without changing DOM structure/styles.
import {AccordionDetails, Box, Typography, Divider} from '@mui/material';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import ArrRadio from '../../../../../components/common/arr-radio/ArrRadio';
import {useTranslation, Trans} from 'react-i18next';
import FileTypeSelector from '../../../../common/arr-fileTypeSelector/FileTypeSelector';
import './NativeComponent.scss';
import ArrFilterChip from '../../../../common/arr-filter-chip/ArrFilterChip';
import TagsTreePanel from '../../common/tags-tree-panel/TagsTreePanel';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {
  FileTypeItem,
  MetaDataField,
  TagItem,
  TreeNode,
} from '../../../../../api/types/production.type';
import {
  useGetNativeComponent,
  useGetTemplateNativeComponent,
} from '../../../../../hooks/productions/useProductions';
import {
  ModuleType,
  FormMode,
  getInsertFieldTypes,
  SlipSheetTreeId,
} from '../../CreateNewProductionUtils';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {setIsFieldsChanged} from '../../../../../redux/slice/productionSlice';
import {transformTagTreeData} from '../../CreateNewProductionHelpers';
import {NativeRedactionsSection} from './NativeRedactionsSection';
import {
  buildSpecifiedRedactionList,
  createDefaultNativeRedactionSettings,
  hydrateNativeRedactionSettings,
  mapMetadataToSelectItems,
  mapNativeRedactionStyleOptions,
  NativeRedactionSettingsState,
  serializeNativeRedactionSettings,
} from './nativeRedactionUtils';

const EMPTY_FILE_GROUP_LIST: FileTypeItem[] = [];
const EMPTY_TAG_TREE_DATA: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
const EMPTY_REDACTION_TAG_LIST: TreeNode[] = [];
const EMPTY_NATIVE_REDACTION_STYLE_LIST: {value: string; text: string}[] = [];
const EMPTY_METADATA_LIST: MetaDataField[] = [];

interface NativeComponentProps {
  fileGroupList?: FileTypeItem[];
  tagTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  redactionTagList?: TreeNode[];
  nativeRedactionStyleList?: {value: string; text: string}[];
  commonMetaDataList?: MetaDataField[];
  commonProductionFieldList?: MetaDataField[];
  productionId: number;
  isProductionSet: boolean;
  onFileGroupsAndTagsChange?: (fileGroups: string, tags: string) => void;
  onRedactionSettingsChange?: (
    redactionSettings: NativeRedactionSettingsState
  ) => void;
  onNativeDirtyChange?: (isDirty: boolean) => void;
  onNativeBaselineReady?: () => void;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
}

export const NativeComponent: React.FC<NativeComponentProps> = ({
  fileGroupList = EMPTY_FILE_GROUP_LIST,
  tagTreeData = EMPTY_TAG_TREE_DATA,
  redactionTagList = EMPTY_REDACTION_TAG_LIST,
  nativeRedactionStyleList = EMPTY_NATIVE_REDACTION_STYLE_LIST,
  commonMetaDataList = EMPTY_METADATA_LIST,
  commonProductionFieldList = EMPTY_METADATA_LIST,
  productionId,
  isProductionSet,
  onFileGroupsAndTagsChange,
  onRedactionSettingsChange,
  onNativeDirtyChange,
  onNativeBaselineReady,
  mode = FormMode.CREATE,
  isTemplate = false,
  templateId,
}) => {
  const {t} = useTranslation();
  const isViewMode = mode === FormMode.VIEW;
  const nativeComponentRef = useRef<HTMLDivElement | null>(null);
  const nativeFileTypeSelectorRef = useRef<HTMLDivElement | null>(null);
  const nativeTagsSelectorRef = useRef<HTMLDivElement | null>(null);
  const nativeViewModeFocusRingTargetsRef = useRef<
    Array<{
      element: HTMLElement;
      focusHandler: EventListener;
      blurHandler: EventListener;
    }>
  >([]);
  const nativeViewModeDisabledA11yProps = isViewMode
    ? ({
        'aria-disabled': true,
        tabIndex: -1,
      } as const)
    : {};
  const getNativeInfoIconA11yProps = (label: string) => ({
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': label,
  });

  const options = useMemo(
    () => [
      {
        label: t(
          'productionsAndExports.createNewProduction.productionComponents.native.parentsOfPrivilegedAndRedacted'
        ),
        value: '0',
      },
      {
        label: t(
          'productionsAndExports.createNewProduction.productionComponents.native.completeFamiliesOfPrivilegedAndRedacted'
        ),
        value: '1',
      },
    ],
    [t]
  );

  // Get Redux state
  const selectedSecurityGroupId = useSelector(
    (state: RootState) => state.production.securityGroupId
  );

  // Determine moduleType from isProductionSet using enum
  const moduleType = useMemo(
    () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
    [isProductionSet]
  );

  // API call to get native component data
  const {
    data: nativeComponentDataFromProduction,
    isLoading: isNativeComponentLoadingFromProduction,
    error: nativeComponentErrorFromProduction,
  } = useGetNativeComponent(
    {
      SecurityGroupId: selectedSecurityGroupId,
      moduletype: moduleType,
      productionId: productionId,
      exportProductionId: 0,
    },
    !!selectedSecurityGroupId && !isTemplate && productionId > 0
  );

  const {
    data: nativeComponentDataFromTemplate,
    isLoading: isNativeComponentLoadingFromTemplate,
    error: nativeComponentErrorFromTemplate,
  } = useGetTemplateNativeComponent(
    {
      templateId: templateId!,
      securityGroupId: selectedSecurityGroupId,
    },
    !!selectedSecurityGroupId && isTemplate && !!templateId
  );

  // Use the appropriate data based on isTemplate flag
  const nativeComponentData = isTemplate
    ? nativeComponentDataFromTemplate
    : nativeComponentDataFromProduction;
  const isNativeComponentLoading = isTemplate
    ? isNativeComponentLoadingFromTemplate
    : isNativeComponentLoadingFromProduction;
  const nativeComponentError = isTemplate
    ? nativeComponentErrorFromTemplate
    : nativeComponentErrorFromProduction;
  const dispatch = useDispatch();

  const redactionTagTreeData = useMemo(
    () =>
      redactionTagList.length > 0
        ? transformTagTreeData(
            redactionTagList,
            SlipSheetTreeId.REDACTION_TAG_TREE
          )
        : EMPTY_TAG_TREE_DATA,
    [redactionTagList]
  );

  const redactionStyleOptions = useMemo(
    () => mapNativeRedactionStyleOptions(nativeRedactionStyleList),
    [nativeRedactionStyleList]
  );

  const insertFieldTypes = useMemo(() => getInsertFieldTypes(), []);
  const metadataFieldOptions = useMemo(
    () => mapMetadataToSelectItems(commonMetaDataList),
    [commonMetaDataList]
  );
  const productionFieldOptions = useMemo(
    () => mapMetadataToSelectItems(commonProductionFieldList),
    [commonProductionFieldList]
  );

  const [generateNative, setGenerateNative] = useState(false);
  const [exportNative, setExportNative] = useState(false);
  const [selected, setSelected] = useState('');

  const [isTagsFlyoutOpen, setIsTagsFlyoutOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedObjects, setSelectedObjects] = useState<
    ExtendedTreeItemProps[]
  >([]);
  const [selectedFileTypeIds, setSelectedFileTypeIds] = useState<string[]>([]);
  const [, setComponentIsDirty] = useState<boolean>(false);
  const [redactionSettings, setRedactionSettings] =
    useState<NativeRedactionSettingsState>(
      createDefaultNativeRedactionSettings
    );
  const [redactionGlobalSelectedTags, setRedactionGlobalSelectedTags] =
    useState<TagItem[]>([]);
  const [isRedactionWithoutTags, setIsRedactionWithoutTags] =
    useState<boolean>(true);
  const previousSelectedIdsRef = useRef<string[]>([]);
  const initialNativeConfigRef = useRef<string>('');
  const isBaselineReadyRef = useRef(false);
  const baselineReadyNotifiedRef = useRef(false);
  const lastNotifiedRedactionSettingsRef = useRef<string>('');

  const getInitialFileTypeIds = useCallback((): string[] => {
    if (nativeComponentData?.nativeSelectedFileGroups) {
      return nativeComponentData.nativeSelectedFileGroups
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    }

    return fileGroupList
      .filter((item) => item.isFileTypeSelected)
      .map((item) => item.fileTypeID.toString());
  }, [fileGroupList, nativeComponentData?.nativeSelectedFileGroups]);

  const buildNativeSnapshot = (
    fileTypeIds: string[],
    tagIds: string[],
    produceLoadFile: boolean,
    produceNativeForTechDocs: boolean,
    familyValue: string,
    currentRedactionSettings = redactionSettings,
    currentRedactionGlobalTags = redactionGlobalSelectedTags,
    currentIsRedactionWithoutTags = isRedactionWithoutTags
  ) =>
    JSON.stringify({
      fileTypes: [...fileTypeIds].sort(),
      tags: [...tagIds].map((x) => String(x)).sort(),
      produceLoadFile,
      produceNativeForTechDocs,
      familyValue: familyValue || '0',
      redactionSettings: serializeNativeRedactionSettings(
        currentRedactionSettings,
        currentRedactionGlobalTags,
        currentIsRedactionWithoutTags,
        nativeRedactionStyleList
      ),
    });

  const computeNativeDirty = (
    fileTypeIds = selectedFileTypeIds,
    tagIds = selectedObjects.map((obj) => String(obj.id)),
    produceLoadFile = generateNative,
    produceNativeForTechDocs = exportNative,
    familyValue = selected === '1' ? '1' : '0',
    currentRedactionSettings = redactionSettings,
    currentRedactionGlobalTags = redactionGlobalSelectedTags,
    currentIsRedactionWithoutTags = isRedactionWithoutTags
  ) =>
    Boolean(initialNativeConfigRef.current) &&
    buildNativeSnapshot(
      fileTypeIds,
      tagIds,
      produceLoadFile,
      produceNativeForTechDocs,
      familyValue,
      currentRedactionSettings,
      currentRedactionGlobalTags,
      currentIsRedactionWithoutTags
    ) !== initialNativeConfigRef.current;

  const updateNativeDirty = (
    fileTypeIds = selectedFileTypeIds,
    tagIds = selectedObjects.map((obj) => String(obj.id)),
    produceLoadFile = generateNative,
    produceNativeForTechDocs = exportNative,
    familyValue = selected === '1' ? '1' : '0',
    currentRedactionSettings = redactionSettings,
    currentRedactionGlobalTags = redactionGlobalSelectedTags,
    currentIsRedactionWithoutTags = isRedactionWithoutTags
  ) => {
    if (!isBaselineReadyRef.current) {
      return;
    }

    const dirty = computeNativeDirty(
      fileTypeIds,
      tagIds,
      produceLoadFile,
      produceNativeForTechDocs,
      familyValue,
      currentRedactionSettings,
      currentRedactionGlobalTags,
      currentIsRedactionWithoutTags
    );

    setComponentIsDirty((prev) => {
      if (prev === dirty) {
        return prev;
      }

      onNativeDirtyChange?.(dirty);
      return dirty;
    });

    if (dirty) {
      dispatch(setIsFieldsChanged({isFieldsChanged: true}));
    }
  };

  // Function to get current Native configuration
  const getCurrentConfig = () => {
    // Get selected file type IDs as comma-separated string from state
    const selectedFileTypeIdsString = selectedFileTypeIds.join(',');

    // Get selected tag IDs as comma-separated string
    const selectedTagIds = selectedObjects.map((obj) => obj.id).join(',');

    return {
      isSelectAllFileTypes: false, // TODO: Extract from Native component checkbox
      produceLoadFile: generateNative,
      generateHTMLforEmails: false, // TODO: Extract from Native component checkbox
      produceNativeForTechDocs: exportNative,
      producedLockedFiles: false, // TODO: Extract from Native component checkbox
      protectionOnExcelFile: false, // TODO: Extract from Native component checkbox
      nativeComponentmodel: '', // TODO: Extract from Native component input
      nativeSelectedFileGroups: selectedFileTypeIdsString
        ? `,${selectedFileTypeIdsString}`
        : '',
      nativeSelectedTags: selectedTagIds,
      eRedactedAndPrevilededFamily:
        selected === '0' ? 0 : selected === '1' ? 1 : 0,
      redactionSettings: serializeNativeRedactionSettings(
        redactionSettings,
        redactionGlobalSelectedTags,
        isRedactionWithoutTags,
        nativeRedactionStyleList
      ),
    };
  };

  // Expose getCurrentConfig function globally for parent to access
  (window as any).getNativeConfig = getCurrentConfig;

  useEffect(() => {
    const specifiedRedactionList = buildSpecifiedRedactionList(
      redactionGlobalSelectedTags,
      isRedactionWithoutTags
    );

    setRedactionSettings((prev) => {
      if (prev.specifiedRedactionList === specifiedRedactionList) {
        return prev;
      }

      return {
        ...prev,
        specifiedRedactionList,
      };
    });
  }, [redactionGlobalSelectedTags, isRedactionWithoutTags]);

  useEffect(() => {
    const serialized = JSON.stringify({
      isBurnRedactionSelected: redactionSettings.isBurnRedactionSelected,
      redactionType: redactionSettings.redactionType,
      specifiedRedactionList: redactionSettings.specifiedRedactionList,
    });
    if (lastNotifiedRedactionSettingsRef.current === serialized) {
      return;
    }
    lastNotifiedRedactionSettingsRef.current = serialized;
    onRedactionSettingsChange?.(redactionSettings);
  }, [redactionSettings, onRedactionSettingsChange]);

  const handleRedactionSettingsChange = (
    nextSettings: NativeRedactionSettingsState
  ) => {
    if (isViewMode) return;
    setRedactionSettings(nextSettings);
    updateNativeDirty(
      selectedFileTypeIds,
      selectedObjects.map((obj) => String(obj.id)),
      generateNative,
      exportNative,
      selected,
      nextSettings,
      redactionGlobalSelectedTags,
      isRedactionWithoutTags
    );
  };

  const handleRedactionGlobalTagsChange = (tags: TagItem[]) => {
    if (isViewMode) return;
    setRedactionGlobalSelectedTags(tags);
    updateNativeDirty(
      selectedFileTypeIds,
      selectedObjects.map((obj) => String(obj.id)),
      generateNative,
      exportNative,
      selected,
      redactionSettings,
      tags,
      isRedactionWithoutTags
    );
  };

  const handleRedactionWithoutTagsChange = (checked: boolean) => {
    if (isViewMode) return;
    setIsRedactionWithoutTags(checked);
    updateNativeDirty(
      selectedFileTypeIds,
      selectedObjects.map((obj) => String(obj.id)),
      generateNative,
      exportNative,
      selected,
      redactionSettings,
      redactionGlobalSelectedTags,
      checked
    );
  };

  // Hydrate from API and establish baseline before paint so mount callbacks are not treated as edits.
  useLayoutEffect(() => {
    if (!fileGroupList?.length) {
      isBaselineReadyRef.current = false;
      baselineReadyNotifiedRef.current = false;
      return;
    }

    if (!nativeComponentData) {
      const preSelected = fileGroupList
        .filter((item) => item.isFileTypeSelected)
        .map((item) => item.fileTypeID.toString());
      setSelectedFileTypeIds(preSelected);
      previousSelectedIdsRef.current = preSelected;
      initialNativeConfigRef.current = buildNativeSnapshot(
        preSelected,
        [],
        false,
        false,
        '0',
        createDefaultNativeRedactionSettings(),
        [],
        true
      );
      isBaselineReadyRef.current = false;
      baselineReadyNotifiedRef.current = false;
      return;
    }

    const initialFileTypeIds = getInitialFileTypeIds();
    const initialTagIds = (nativeComponentData.nativeSelectedTags || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    const initialFamilyValue =
      nativeComponentData.eRedactedAndPrevilededFamily === 1 ? '1' : '0';

    if (initialTagIds.length > 0 && tagTreeData.length === 0) {
      isBaselineReadyRef.current = false;
      return;
    }

    setGenerateNative(nativeComponentData.produceLoadFile || false);
    setExportNative(nativeComponentData.produceNativeForTechDocs || false);
    setSelected(initialFamilyValue);
    setSelectedFileTypeIds(initialFileTypeIds);
    previousSelectedIdsRef.current = initialFileTypeIds;

    if (initialTagIds.length > 0) {
      setSelectedIds(initialTagIds);

      const flattenTree = (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
      ): ExtendedTreeItemProps[] =>
        nodes.reduce<ExtendedTreeItemProps[]>((acc, node) => {
          acc.push(node);
          if (node.children) acc.push(...flattenTree(node.children));
          return acc;
        }, []);

      const flatTree = flattenTree(tagTreeData);
      setSelectedObjects(
        flatTree.filter((node) => initialTagIds.includes(node.id))
      );
    } else {
      setSelectedIds([]);
      setSelectedObjects([]);
    }

    const hydratedRedactions = hydrateNativeRedactionSettings(
      nativeComponentData.redactionSettings,
      redactionTagTreeData
    );
    setRedactionSettings(hydratedRedactions.redactionSettings);
    setRedactionGlobalSelectedTags(
      hydratedRedactions.redactionGlobalSelectedTags
    );
    setIsRedactionWithoutTags(hydratedRedactions.isRedactionWithoutTags);

    initialNativeConfigRef.current = buildNativeSnapshot(
      initialFileTypeIds,
      initialTagIds,
      Boolean(nativeComponentData.produceLoadFile),
      Boolean(nativeComponentData.produceNativeForTechDocs),
      initialFamilyValue,
      hydratedRedactions.redactionSettings,
      hydratedRedactions.redactionGlobalSelectedTags,
      hydratedRedactions.isRedactionWithoutTags
    );

    isBaselineReadyRef.current = false;
    baselineReadyNotifiedRef.current = false;
    setComponentIsDirty(false);
  }, [
    nativeComponentData,
    fileGroupList,
    tagTreeData,
    redactionTagTreeData,
    getInitialFileTypeIds,
  ]);

  useEffect(() => {
    if (!initialNativeConfigRef.current) {
      return;
    }

    const finalizeBaseline = () => {
      if (!initialNativeConfigRef.current) {
        return;
      }

      const dirty = nativeComponentData ? computeNativeDirty() : false;

      if (!isBaselineReadyRef.current) {
        isBaselineReadyRef.current = true;
        setComponentIsDirty(dirty);

        if (dirty) {
          baselineReadyNotifiedRef.current = true;
          onNativeDirtyChange?.(true);
          dispatch(setIsFieldsChanged({isFieldsChanged: true}));
        } else {
          baselineReadyNotifiedRef.current = true;
          onNativeDirtyChange?.(false);
          onNativeBaselineReady?.();
        }
        return;
      }

      if (nativeComponentData) {
        updateNativeDirty();
      }
    };

    // Defer until after FileTypeSelector mount effects (which may emit [] first).
    const frameId = window.requestAnimationFrame(finalizeBaseline);
    return () => window.cancelAnimationFrame(frameId);
  }, [
    nativeComponentData,
    selectedFileTypeIds,
    selectedObjects,
    generateNative,
    exportNative,
    selected,
    redactionSettings,
    redactionGlobalSelectedTags,
    isRedactionWithoutTags,
    onNativeDirtyChange,
    onNativeBaselineReady,
  ]);

  // Notify parent when file groups or tags change (for form validation)
  // This follows the same pattern as DAT's onFieldsChange
  useEffect(() => {
    if (onFileGroupsAndTagsChange) {
      const selectedFileTypeIdsString = selectedFileTypeIds.join(',');
      const fileGroups = selectedFileTypeIdsString
        ? `,${selectedFileTypeIdsString}`
        : '';
      const tags = selectedObjects.map((obj) => obj.id).join(',');
      onFileGroupsAndTagsChange(fileGroups, tags);
    }
  }, [selectedFileTypeIds, selectedObjects, onFileGroupsAndTagsChange]);

  const handleToggleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (isViewMode) return;
    setGenerateNative(checked);
    updateNativeDirty(
      selectedFileTypeIds,
      selectedObjects.map((obj) => String(obj.id)),
      checked,
      exportNative,
      selected
    );
  };
  const handleExportChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (isViewMode) return;
    setExportNative(checked);
    updateNativeDirty(
      selectedFileTypeIds,
      selectedObjects.map((obj) => String(obj.id)),
      generateNative,
      checked,
      selected
    );
  };

  // Tags are now managed through selectedObjects from API response

  const handleTags = (data: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
    if (isViewMode) return;
    const selectedIds = data.map((item) => item.id);
    setSelectedIds(selectedIds);

    const flattenTree = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
    ): ExtendedTreeItemProps[] => {
      return nodes.reduce<ExtendedTreeItemProps[]>((acc, node) => {
        acc.push(node);
        if (node.children) acc.push(...flattenTree(node.children));
        return acc;
      }, []);
    };

    const flatTree = flattenTree(tagTreeData);
    const selectedObjects = flatTree.filter((node) =>
      selectedIds.includes(node.id)
    );

    setSelectedObjects(selectedObjects);
    updateNativeDirty(
      selectedFileTypeIds,
      selectedObjects.map((obj) => String(obj.id)),
      generateNative,
      exportNative,
      selected
    );
  };

  const handleRemove = (value: string, type: 'id' | 'label' = 'id') => {
    if (isViewMode) return;
    const updated = selectedObjects.filter((item) =>
      type === 'id' ? item.id !== value : item.label !== value
    );
    setSelectedObjects(updated);
    // Also update selectedIds to keep them in sync
    const updatedIds = updated.map((obj) => obj.id);
    setSelectedIds(updatedIds);
    updateNativeDirty(
      selectedFileTypeIds,
      updated.map((obj) => String(obj.id)),
      generateNative,
      exportNative,
      selected
    );
  };

  // Handle file type selection changes

  // Memoizing handleFileTypeSelectionChange
  const handleFileTypeSelectionChange = useCallback(
    (newSelectedIds: string[]) => {
      if (isViewMode) {
        return;
      }

      // Block FileTypeSelector's initial empty notification before hydration settles.
      if (!isBaselineReadyRef.current && newSelectedIds.length === 0) {
        return;
      }

      const normalizeIds = (ids: string[]) => [...ids].sort().join(',');
      const isSameSelection =
        normalizeIds(newSelectedIds) ===
        normalizeIds(previousSelectedIdsRef.current);

      if (isSameSelection) {
        return;
      }

      previousSelectedIdsRef.current = newSelectedIds;
      setSelectedFileTypeIds(newSelectedIds);
      updateNativeDirty(newSelectedIds);
    },
    [isViewMode]
  );

  // Use fileGroupList from props (from getProductionComponents API) and apply selections from nativeComponentData
  const availableFileTypes = useMemo(() => {
    // Use fileGroupList from props as the base file types
    const baseFileTypes = fileGroupList;

    // If we have nativeSelectedFileGroups from getNativeComponent API, update the isFileTypeSelected property
    if (nativeComponentData?.nativeSelectedFileGroups) {
      const selectedIds = nativeComponentData.nativeSelectedFileGroups
        .split(',')
        .map((id) => id.trim());
      return baseFileTypes.map((fileType) => ({
        ...fileType,
        isFileTypeSelected: selectedIds.includes(
          fileType.fileTypeID.toString()
        ),
      }));
    }

    return baseFileTypes;
  }, [nativeComponentData, fileGroupList]);

  useEffect(() => {
    if (!isViewMode) {
      const tagContainer = nativeTagsSelectorRef.current;
      if (!tagContainer) return;

      selectedObjects.forEach((item, index) => {
        const chipElement = tagContainer.querySelector<HTMLElement>(
          `[data-testid="selected-types-${index}"]`
        );
        const removeControl = chipElement?.querySelector<HTMLElement>(
          'button, [role="button"], [tabindex]'
        );
        if (removeControl) {
          removeControl.setAttribute('aria-label', `Remove ${item.label}`);
        }
      });
      return;
    }

    let animationFrameId = 0;

    const getControlText = (element: HTMLElement): string =>
      (element.getAttribute('aria-label') || element.textContent || '')
        .trim()
        .replace(/\s+/g, ' ');

    const setViewModeFocusable = (
      element: HTMLElement | null | undefined,
      ariaLabel?: string
    ) => {
      if (!element) {
        return;
      }

      if (element.getAttribute('tabindex') !== '0') {
        element.setAttribute('tabindex', '0');
      }

      if (element.getAttribute('aria-disabled') !== 'true') {
        element.setAttribute('aria-disabled', 'true');
      }

      if (ariaLabel && element.getAttribute('aria-label') !== ariaLabel) {
        element.setAttribute('aria-label', ariaLabel);
      }

      const hasExistingFocusHandler =
        nativeViewModeFocusRingTargetsRef.current.some(
          (entry) => entry.element === element
        );

      if (!hasExistingFocusHandler) {
        const focusHandler = () => {
          element.style.outline = '2px solid #fff';
          element.style.outlineOffset = '2px';
          element.style.borderRadius = element.style.borderRadius || '4px';
        };
        const blurHandler = () => {
          element.style.outline = '';
          element.style.outlineOffset = '';
        };

        element.addEventListener('focus', focusHandler);
        element.addEventListener('blur', blurHandler);
        nativeViewModeFocusRingTargetsRef.current.push({
          element,
          focusHandler,
          blurHandler,
        });
      }
    };

    const setViewModeNonFocusable = (
      element: HTMLElement | null | undefined
    ) => {
      if (!element) {
        return;
      }

      if (element.getAttribute('tabindex') !== '-1') {
        element.setAttribute('tabindex', '-1');
      }
    };

    const getSelectedFileTypeLabels = (): string[] => {
      const selectedIdSet = new Set(selectedFileTypeIds.map(String));

      return (availableFileTypes || [])
        .filter(
          (fileType) =>
            fileType.isFileTypeSelected ||
            selectedIdSet.has(String(fileType.fileTypeID))
        )
        .map((fileType) => fileType.fileTypeLabel)
        .filter(Boolean);
    };

    const applyNativeViewModeFocus = () => {
      const container = nativeComponentRef.current;
      if (!container) {
        return;
      }

      const focusableElements = container.querySelectorAll<HTMLElement>(
        'button, input, select, textarea, a[href], [role="button"], [role="radio"], [role="checkbox"], [role="switch"], [role="combobox"], [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
      );

      focusableElements.forEach((element) => {
        const isInfoIcon =
          element.classList.contains('view-user-info-icon') &&
          element.getAttribute('role') === 'button';

        if (isInfoIcon) {
          setViewModeFocusable(element);
          return;
        }

        setViewModeNonFocusable(element);
      });

      const selectedFileTypeLabels = getSelectedFileTypeLabels();
      if (selectedFileTypeLabels.length > 0) {
        nativeFileTypeSelectorRef.current
          ?.querySelectorAll<HTMLElement>(
            'button, input, select, textarea, a[href], [role="button"], [role="checkbox"], [role="combobox"], [tabindex]'
          )
          .forEach(setViewModeNonFocusable);

        setViewModeFocusable(
          nativeFileTypeSelectorRef.current,
          `${t(
            'productionsAndExports.createNewProduction.productionComponents.native.selectFileTags'
          )} ${selectedFileTypeLabels.join(', ')}`
        );
      } else {
        setViewModeNonFocusable(nativeFileTypeSelectorRef.current);
      }

      selectedObjects.forEach((item, index) => {
        const chipElement =
          nativeTagsSelectorRef.current?.querySelector<HTMLElement>(
            `[data-testid="selected-types-${index}"]`
          );

        if (!chipElement) {
          return;
        }

        chipElement
          .querySelectorAll<HTMLElement>('button, [role="button"], [tabindex]')
          .forEach((nestedElement) => {
            if (nestedElement !== chipElement) {
              setViewModeNonFocusable(nestedElement);
            }
          });

        setViewModeFocusable(chipElement, String(item.label));
      });

      container
        .querySelectorAll<HTMLInputElement>('input[type="radio"]')
        .forEach((radioElement) => {
          if (!radioElement.checked) {
            return;
          }

          const radioFocusTarget =
            radioElement.closest<HTMLElement>('label') ??
            radioElement.closest<HTMLElement>('[data-testid]') ??
            radioElement;

          setViewModeFocusable(
            radioFocusTarget,
            getControlText(radioFocusTarget)
          );
        });

      container
        .querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
        .forEach((checkboxElement) => {
          if (!checkboxElement.checked) {
            return;
          }

          const checkboxFocusTarget =
            checkboxElement.closest<HTMLElement>('label') ??
            checkboxElement.closest<HTMLElement>('[data-testid]') ??
            checkboxElement;

          setViewModeFocusable(
            checkboxFocusTarget,
            getControlText(checkboxFocusTarget)
          );
        });

      container
        .querySelectorAll<HTMLInputElement>(
          'input[type="checkbox"][role="switch"], [role="switch"]'
        )
        .forEach((switchElement) => {
          const inputElement = switchElement as HTMLInputElement;
          const isChecked =
            inputElement.checked ||
            switchElement.getAttribute('aria-checked') === 'true';

          if (!isChecked) {
            return;
          }

          const switchFocusTarget =
            switchElement.closest<HTMLElement>('label') ??
            switchElement.closest<HTMLElement>('.native-toggle') ??
            switchElement.closest<HTMLElement>('[data-testid]') ??
            switchElement;

          setViewModeFocusable(
            switchFocusTarget,
            getControlText(switchFocusTarget)
          );
        });
    };

    const scheduleFocusUpdate = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(applyNativeViewModeFocus);
    };

    scheduleFocusUpdate();

    const observer = new MutationObserver(scheduleFocusUpdate);
    if (nativeComponentRef.current) {
      observer.observe(nativeComponentRef.current, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['tabindex', 'disabled', 'aria-disabled', 'value'],
      });
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      observer.disconnect();
      nativeViewModeFocusRingTargetsRef.current.forEach(
        ({element, focusHandler, blurHandler}) => {
          element.removeEventListener('focus', focusHandler);
          element.removeEventListener('blur', blurHandler);
          element.style.outline = '';
          element.style.outlineOffset = '';
        }
      );
      nativeViewModeFocusRingTargetsRef.current = [];
    };
  }, [
    isViewMode,
    availableFileTypes,
    selectedFileTypeIds,
    selectedObjects,
    selected,
    exportNative,
    generateNative,
    t,
  ]);

  // Show loading state
  if (isNativeComponentLoading) {
    return (
      <Box className="native-component">
        <Typography
          data-testid={`native-component-loading-native-component-data`}
        >
          Loading native component data...
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (nativeComponentError) {
    return (
      <Box className="native-component">
        <Typography
          color="error"
          data-testid={`native-component-error-loading-native-component-data`}
        >
          Error loading native component data: {nativeComponentError.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="native-component native-accordion"
      data-test-id={'NativeAccordion'}
      ref={nativeComponentRef}
    >
      <AccordionDetails className="native-accordion-details">
        <Box className="native-component-content">
          <Box className="native-intro-section">
            <div
              className="produce-native-files"
              data-testid={`native-component-produce-native-file-produce-native-description-produce-native-description1`}
            >
              <div
                className="produce-header"
                data-testid={`native-component-produce-native-file`}
              >
                <Typography
                  variant="subtitle1"
                  className="produce-title"
                  data-testid={`native-component-produce-native-file-text`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponent.produceNativeFile'
                  )}
                </Typography>
                <ArrTooltipWrapper
                  title="Produce Native Files"
                  closeTooltipOnEscPress
                  trigger={
                    <InfoIcon
                      className="view-user-info-icon"
                      {...getNativeInfoIconA11yProps(
                        'Produce Native Files info'
                      )}
                    />
                  }
                >
                  <Trans
                    i18nKey="productionsAndExports.createNewProduction.productionComponents.native.nativeDocumentSpecificationDescription"
                    components={{
                      br: <br data-testid={`native-component-br`} />,
                    }}
                  />
                </ArrTooltipWrapper>
              </div>
              <Box className="produce-description">
                <Typography
                  className="typo-text body3_size"
                  data-testid={`native-component-produce-native-description`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponent.produceNativeDescription'
                  )}
                </Typography>

                <Typography
                  className="typo-text body3_size"
                  data-testid={`native-component-produce-native-description1`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponent.produceNativeDescription1'
                  )}
                </Typography>
              </Box>
            </div>
          </Box>
          <Divider className="native-divider" />
          <Box className="native-file-types-section">
            <div
              className="tag-files"
              data-testid={`native-component-select-file-tags`}
            >
              <Typography
                variant="subtitle1"
                className=" heading typo-text subhead4_size"
                data-testid={`native-component-select-file-tags-text`}
              >
                {t(
                  'productionsAndExports.createNewProduction.productionComponents.native.selectFileTags'
                )}
              </Typography>
            </div>
            <Box className="native-filetag-container">
              <Box className="native-fileType" ref={nativeFileTypeSelectorRef}>
                <FileTypeSelector
                  fileGroupList={availableFileTypes}
                  onSelectionChange={handleFileTypeSelectionChange}
                  {...nativeViewModeDisabledA11yProps}
                  disabled={isViewMode}
                  checkboxDataTestId="native-component-file-type-selector"
                />
              </Box>

              <Box className="native-fileType" ref={nativeTagsSelectorRef}>
                <div
                  className="filetype-selector"
                  data-testid={`native-component-tags-add-tags`}
                >
                  <div
                    className="filetype-header"
                    data-testid={`native-component-tags-add-tags-btn`}
                  >
                    <Typography
                      className="typo-text subhead5_size filetype-title"
                      data-testid={`native-component-tags`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponent.tags'
                      )}
                    </Typography>
                    <ArrButton
                      variant="primary"
                      size="small"
                      dataTestId="native-component-add-tags-button"
                      onClick={() => !isViewMode && setIsTagsFlyoutOpen(true)}
                      {...nativeViewModeDisabledA11yProps}
                      disabled={isViewMode}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponent.addTags'
                      )}
                    </ArrButton>
                  </div>

                  <div
                    className="selected-types"
                    data-testid={`native-component-div`}
                  >
                    {selectedObjects.map((item, index) => {
                      return (
                        <ArrFilterChip
                          key={item.id}
                          classNames="chip"
                          filterName=""
                          selections={[{id: item.id, value: item.label}]}
                          variant="v1"
                          onRemove={() => handleRemove(item.id)}
                          aria-label={
                            !isViewMode ? `Remove ${item.label}` : undefined
                          }
                          disabled={isViewMode}
                          dataTestId={`selected-types-${index}`}
                        />
                      );
                    })}
                  </div>
                </div>
                {isTagsFlyoutOpen && (
                  <TagsTreePanel
                    title={t(
                      'productionsAndExports.createNewProduction.datComponent.selectTags'
                    )}
                    tooltip={{
                      title: t(
                        'productionsAndExports.createNewProduction.productionComponents.native.tooltips.selectTags.title'
                      ),
                      content: t(
                        'productionsAndExports.createNewProduction.productionComponents.native.tooltips.selectTags.content'
                      ),
                    }}
                    type="flyout"
                    tags={tagTreeData}
                    selectedTags={selectedObjects}
                    isOpen={isTagsFlyoutOpen}
                    onClose={() => setIsTagsFlyoutOpen(false)}
                    setSelectedTags={(items) => {
                      handleTags(items);
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Divider className="native-divider" />

          <Box className="native-withhold-section">
            <Box className="native-section">
              <Typography
                variant="subtitle1"
                className="typo-text subhead4_size section-title"
                data-testid={`native-component-dont-produced-native`}
              >
                {t(
                  'productionsAndExports.createNewProduction.productionComponents.native.dontProducedNative'
                )}
              </Typography>
              <ArrTooltipWrapper
                closeTooltipOnEscPress
                title="Privileged and Redacted Document Family Members"
                trigger={
                  <InfoIcon
                    className="view-user-info-icon"
                    {...getNativeInfoIconA11yProps(
                      'Privileged and Redacted Document Family Members info'
                    )}
                  />
                }
              >
                <Trans
                  i18nKey="productionsAndExports.createNewProduction.productionComponents.native.dontProducedNativeDescription"
                  components={{
                    br: <br data-testid={`native-component-br-btn`} />,
                    strong: <strong data-testid={`native-component-strong`} />,
                    p: <p data-testid={`native-component-p`} />,
                  }}
                />
              </ArrTooltipWrapper>
            </Box>

            <Box className="native-withhold-inputs">
              <Box className="native-radio-group">
                <ArrRadio
                  dataTestId="controlled-radio-buttons-group"
                  size="small"
                  name="controlled-radio-buttons-group"
                  onChange={(value) => {
                    if (isViewMode) return;
                    setSelected(value);
                    updateNativeDirty(
                      selectedFileTypeIds,
                      selectedObjects.map((obj) => String(obj.id)),
                      generateNative,
                      exportNative,
                      value
                    );
                  }}
                  value={selected}
                  data={options}
                  className="section-title custom-label"
                  {...nativeViewModeDisabledA11yProps}
                  disabled={isViewMode}
                />
              </Box>
              <Box className="produce-description native-note">
                <Typography
                  className="typo-text body3_size"
                  data-testid={`native-component-note-note-content`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponent.note'
                  )}
                  :{' '}
                  {t(
                    'productionsAndExports.createNewProduction.productionComponent.noteContent'
                  )}
                </Typography>
              </Box>

              <Box className="native-toggle">
                <ArrToggle
                  role="switch"
                  aria-checked={exportNative}
                  {...nativeViewModeDisabledA11yProps}
                  disabled={isViewMode}
                  checked={exportNative}
                  onChange={handleExportChange}
                  dataTestId="chkNoNativeForTechIssueTag"
                />
                <Typography
                  className="toggle-text typo-text subhead5_size"
                  data-testid={`native-component-dont-export-native`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponents.native.dontExportNative'
                  )}
                </Typography>
                <ArrTooltipWrapper
                  closeTooltipOnEscPress
                  title="Enable Placeholders by Selecting File Types"
                  trigger={
                    <InfoIcon
                      className="view-user-info-icon"
                      {...getNativeInfoIconA11yProps('Tech Issue Tag info')}
                    />
                  }
                >
                  <Trans
                    i18nKey="productionsAndExports.createNewProduction.productionComponents.native.nativePlaceholdersByFileTypeDescription"
                    components={{
                      br: <br data-testid={`native-component-br-button`} />,
                      strong: (
                        <strong data-testid={`native-component-strong-btn`} />
                      ),
                      p: <p data-testid={`native-component-p-text`} />,
                    }}
                  />
                </ArrTooltipWrapper>
              </Box>
            </Box>
          </Box>
          {/* remove classname once rnfv feature live */}
          <Box className="native-redactions-visual-hide">
            <NativeRedactionsSection
              settings={redactionSettings}
              redactionGlobalSelectedTags={redactionGlobalSelectedTags}
              isRedactionWithoutTags={isRedactionWithoutTags}
              redactionTagTreeData={redactionTagTreeData}
              redactionStyleOptions={redactionStyleOptions}
              insertFieldTypes={insertFieldTypes}
              metadataFieldOptions={metadataFieldOptions}
              productionFieldOptions={productionFieldOptions}
              disabled={isViewMode}
              onSettingsChange={handleRedactionSettingsChange}
              onGlobalTagsChange={handleRedactionGlobalTagsChange}
              onRedactionWithoutTagsChange={handleRedactionWithoutTagsChange}
              getInfoIconA11yProps={getNativeInfoIconA11yProps}
              viewModeDisabledA11yProps={nativeViewModeDisabledA11yProps}
            />
          </Box>

          <Divider className="native-divider" />

          <Box className="native-toggle native-lst-toggle">
            <ArrToggle
              role="switch"
              aria-checked={generateNative}
              {...nativeViewModeDisabledA11yProps}
              disabled={isViewMode}
              checked={generateNative}
              onChange={handleToggleChange}
              dataTestId="native-toggle"
            />
            <Typography
              className="toggle-text typo-text subhead4_size"
              data-testid={`native-component-generate-native`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.native.generateNative'
              )}
            </Typography>
            <ArrTooltipWrapper
              title="Generate Load File"
              closeTooltipOnEscPress
              trigger={
                <InfoIcon
                  className="view-user-info-icon"
                  {...getNativeInfoIconA11yProps(
                    'Generate Native Load File info'
                  )}
                />
              }
            >
              <Trans
                i18nKey="productionsAndExports.createNewProduction.productionComponents.native.generateNativeDescription"
                components={{
                  br: <br data-testid={`native-component-br-icon`} />,
                  strong: (
                    <strong data-testid={`native-component-strong-button`} />
                  ),
                  p: <p data-testid={`native-component-p-btn`} />,
                }}
              />
            </ArrTooltipWrapper>
          </Box>
        </Box>
      </AccordionDetails>
    </Box>
  );
};
